/**
 * Vocabulary Learning Page by Grade - Optimized Version
 */

import { useState, useMemo, useCallback } from 'react';
import { vocabularyCategories } from '../../data/vocabulary';
import { completeVocabularyData, VocabularyWord } from '../../data/vocabulary-index';
import WordCard from './WordCard';

export default function VocabularyGradePage() {
  const [selectedGrade, setSelectedGrade] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Memoized filtered words
  const filteredWords = useMemo(() => {
    if (!completeVocabularyData || completeVocabularyData.length === 0) {
      return [];
    }

    let words = [...completeVocabularyData];

    // Filter by level
    if (selectedLevel !== 'all') {
      const elementaryCategories = ['animals', 'food', 'family', 'colors', 'numbers', 'body', 'actions', 'school', 'weather', 'emotions', 'daily_life', 'transport', 'places', 'time', 'clothes', 'sports'];
      words = words.filter(word => {
        const wordLevel = word.level || (elementaryCategories.includes(word.category) ? 'elementary' : 'middle');
        return wordLevel === selectedLevel;
      });
    }

    // Filter by grade
    if (selectedGrade !== 'all') {
      words = words.filter(word => {
        if (word.grade) return word.grade === selectedGrade;
        // Infer grade
        const elementaryCategories = ['animals', 'food', 'family', 'colors', 'numbers', 'body', 'actions', 'school', 'weather', 'emotions', 'daily_life', 'transport', 'places', 'time', 'clothes', 'sports'];
        if (elementaryCategories.includes(word.category)) {
          if (word.difficulty === 'easy') {
            const grade3Categories = ['animals', 'family', 'colors', 'numbers', 'body', 'food'];
            return grade3Categories.includes(word.category) ? selectedGrade === '3' : selectedGrade === '4';
          } else {
            return selectedGrade === '5' || selectedGrade === '6';
          }
        }
        return selectedGrade === '7' || selectedGrade === '8' || selectedGrade === '9';
      });
    }

    // Filter by difficulty
    if (selectedDifficulty !== 'all') {
      words = words.filter(word => word.difficulty === selectedDifficulty);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      words = words.filter(word => word.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      words = words.filter(word => {
        // Search in English word
        if (word.word.toLowerCase().includes(query)) return true;
        // Search in Chinese meaning
        if (word.meaning && word.meaning.toLowerCase().includes(query)) return true;
        // Search in example sentence
        if (word.example && word.example.toLowerCase().includes(query)) return true;
        // Search in example translation
        if (word.exampleTranslation && word.exampleTranslation.toLowerCase().includes(query)) return true;
        return false;
      });
    }

    return words;
  }, [selectedLevel, selectedGrade, selectedDifficulty, selectedCategory, searchQuery]);

  // Memoized categories for current level
  const categoriesForLevel = useMemo(() => {
    if (selectedLevel === 'all') {
      return vocabularyCategories;
    }
    return vocabularyCategories.filter(cat => cat.level === selectedLevel);
  }, [selectedLevel]);

  // Callback for category change
  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
  }, []);

  // Callback for level change
  const handleLevelChange = useCallback((level: string) => {
    setSelectedLevel(level);
    setSelectedGrade('all');
    setSelectedCategory('all');
  }, []);

  return (
    <div className="space-y-6">
      {/* Header with Search */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">📚 单词学习</h1>
            <p className="text-white/80">共 {completeVocabularyData.length} 个单词，筛选后: {filteredWords.length} 个</p>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl font-medium transition-all flex items-center gap-2"
          >
            <span>⚙️</span>
            <span>{showFilters ? '收起筛选' : '高级筛选'}</span>
          </button>
        </div>

        {/* Search Box - Always Visible */}
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="🔍 搜索单词 (支持中英文模糊搜索)"
            className="w-full px-6 py-4 pr-12 rounded-2xl bg-white/95 backdrop-blur text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-2xl font-bold"
              title="清空搜索"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Filters - Collapsible */}
      {showFilters && (
        <div className="bg-white rounded-2xl shadow-soft p-6 space-y-4">
          {/* Level Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">学段</label>
            <div className="flex gap-2">
              <button
                onClick={() => handleLevelChange('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedLevel === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                全部 ({completeVocabularyData.length})
              </button>
              <button
                onClick={() => handleLevelChange('elementary')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedLevel === 'elementary' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                🏫 小学
              </button>
              <button
                onClick={() => handleLevelChange('middle')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedLevel === 'middle' ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                🎓 初中
              </button>
            </div>
          </div>

          {/* Grade Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">年级</label>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedGrade('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedGrade === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                全部
              </button>
              {['3', '4', '5', '6'].map(g => (
                <button
                  key={g}
                  onClick={() => setSelectedGrade(g)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedGrade === g ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {g}年级
                </button>
              ))}
              {['7', '8', '9'].map(g => (
                <button
                  key={g}
                  onClick={() => setSelectedGrade(g)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedGrade === g ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {g}年级
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">难度</label>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedDifficulty('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedDifficulty === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                全部
              </button>
              <button
                onClick={() => setSelectedDifficulty('easy')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedDifficulty === 'easy' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                简单
              </button>
              <button
                onClick={() => setSelectedDifficulty('medium')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedDifficulty === 'medium' ? 'bg-yellow-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                中等
              </button>
              <button
                onClick={() => setSelectedDifficulty('hard')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedDifficulty === 'hard' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                困难
              </button>
            </div>
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">分类</label>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleCategoryChange('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedCategory === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                全部
              </button>
              {categoriesForLevel.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedCategory === category.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.icon} {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results count */}
      {!showFilters && (
        <div className="text-gray-600 text-sm font-medium text-center">
          找到 <span className="text-primary font-bold text-lg">{filteredWords.length}</span> 个单词
          {searchQuery && (
            <span className="ml-2 text-gray-500">
              (搜索: "{searchQuery}")
            </span>
          )}
        </div>
      )}

      {showFilters && (
        <div className="text-gray-600 text-sm font-medium">
          找到 <span className="text-primary font-bold text-lg">{filteredWords.length}</span> 个单词
          {selectedCategory !== 'all' && (
            <span className="ml-2 text-gray-500">
              (筛选分类: {selectedCategory})
            </span>
          )}
          {filteredWords.length > 0 && (
            <span className="ml-2 text-gray-400">
              前3个: {filteredWords.slice(0, 3).map(w => w.word).join(', ')}
            </span>
          )}
        </div>
      )}

      {/* Words Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredWords.length > 0 && filteredWords.slice(0, 50).map((word, index) => (
          <WordCard key={`${word.id}-${index}`} word={word} />
        ))}
      </div>

      {/* Empty state */}
      {filteredWords.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <p className="text-gray-500 text-lg">没有找到符合条件的单词</p>
          <p className="text-gray-400 text-sm mt-2">试试调整筛选条件或搜索其他关键词</p>
        </div>
      )}

      {/* More words indicator */}
      {filteredWords.length > 50 && (
        <div className="text-center py-4 text-gray-500">
          显示前50个，共 {filteredWords.length} 个单词
        </div>
      )}
    </div>
  );
}
